import { Container, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { useEffect } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { oneformBFFFilesEmailSend, oneformBFFNdpsSend } from 'src/bff';
import { DetalleMotivo, Email, InputFile, PantallaNombreApellido, Telefono } from 'src/components';
import { FormController, OneElementTemplate, useOneFormContext } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { useTrackingManager } from 'src/feature-app/hooks/use-tracking-manager';
import { formDataBuilder, formatFlags, formatHeaders } from 'src/forms/format-fields';
import { FormDataGroup, FormInfo, OneFormState, Steps } from 'src/types';
import { UGCConfirmacion, UGCThankYou } from '.';

export function UGCForm() {
	const { initializeForm } = useOneFormContext();
	const trackingManager = useTrackingManager();
	const store = useStore();
	const dispatch = useDispatch();
	const { formData }: OneFormState = store.getState();
	const urlParams = new URLSearchParams(window.location.search);
	const dev = urlParams.get('dev') !== null;

	const dpsToken = 'VW_DDB_FRM_UGC';

	const initialData: Partial<FormDataGroup> = {
		headers: {
			DPS_TOKEN: dpsToken,
			FORM_OBJECTIVE: 'User Generated Content',
			FORM_DETAILS: 'UGC',
			FORM_NAME: 'vw-oneform-ugc',
		},
		fields: {
			formName: 'vw-oneform-ugc',
			formType: 'otherform',
      pageCategory: 'User Generated Content',
		},
		flags: {
			PERSON_RGPD_ADAPTED: 'true',
		},
	};

	const generateData = () => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;
		const { tc, today, url, device } = getParamsForm();

		const data = {
			...formatHeaders(initialData.headers),
			'headers[SF_CAMPAIGN_NAME]': 'PENDIENTE',
			'headers[SF_CAMPAIGN_ID]': 'PENDIENTE',
			'headers[FORM_URL]': url,
			'headers[FORM_DATE_INSERTION]': today,
			'headers[FORM_VERSION]': fields.formName ? `vw-oneform-${fields.formName}-${process.env.VERSION}` : `vw-oneform-adhoc_2025_the_originals-${process.env.VERSION}`,
			'headers[ORIGIN_DEVICE_TYPE]': device,
			'headers[ORIGIN_TRACKING_CODE]': tc,

			'fields[DPS_BRAND]': 'Volkswagen',
			'fields[PERSON_NAME]': fields.name,
			'fields[PERSON_SURNAME_1]': fields.surname,
			'fields[PERSON_SURNAME_2]': fields.secondSurname,
			'fields[PERSON_EMAIL]': fields.email,
			'fields[PERSON_PHONE_MOBILE]': fields.cellphone,
			'fields[PERSON_PHONE_MOBILE_FULL]': `${fields.prefix}${fields.cellphone}`,
			'fields[PERSON_PHONE_PREFIX]': fields.prefix,

			'fields[SURVEY_ANSWER_01]': fields.tuHistoria,
			'fields[SURVEY_ANSWER_02]': 'PERMITE',

			'fields[PERSON_RGPD_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',
			'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi ? fields.lssi : 'NO INFORMADO',

			...formatFlags(initialData.flags),
		};

		return data;
	};

	useEffect(() => {
		if (dev) {
			console.log(generateData());
			console.log(store.getState());
		}
	}, [formData]);

	const sendForm = async (): Promise<boolean> => {
		return new Promise<any>(async (resolve, reject) => {
			const data = generateData();
			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formData);

			if (response?.data?.status?.code == 200) {
				await sendFiles();
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};

	const sendFiles = async (): Promise<boolean> => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;

		return new Promise<any>(async (resolve, reject) => {
			const data = generateData();
			const formData = formDataBuilder(data);
			const response = await oneformBFFFilesEmailSend(formData, fields.tusAssets);

			if (response?.data?.status?.code == 200) {
				resolve(true);
			} else {
				resolve(false);
			}
		});
	};


	let index = 0;
	const steps: Steps[] = [
		{
			title: (
				<Container wrap="always">
					<Text>Tu Volkswagen, tu historia: <Text bold>cuéntanosla</Text></Text>
					<Text appearance={TokenTextAppearance.copy200}>Anécdotas, fotos, un vídeo de tu infancia... ¡Comparte con nosotros lo que quieras!</Text>
				</ Container>
			),
			fields: <OneElementTemplate element={
				<>
					<DetalleMotivo required={true} name="tuHistoria" numberOfMaxLength={700} hideCTA />
					<InputFile name="tusAssets" label='Sube tus imágenes o vídeos' CTALabel='Subir archivos' maxMbSize={12} allowedTypes={['video/mp4', 'image/jpeg', 'image/png']} maxFiles={2} />
				</>
				}
			/>,
			screenIndex: index++,
			name: 'TuHistoria',
		},
		{
			title: (
				<>
					¡Menudo viaje! <Text bold>¿Quién firma esta historia?</Text>
				</>
			),
			fields: (
				<OneElementTemplate
					element={
						<div style={{ marginBottom: '2px' }}>
							<PantallaNombreApellido />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'DatosPersonales',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		{
			title: (
				<Container wrap="always">
					<Text>Y si seguimos con curiosidad, <Text bold>¿cómo te encontramos?</Text></Text>
					<Text appearance={TokenTextAppearance.copy200}>Hay historias que nos dejan sin palabras y quizá queremos preguntarte un poquito más.</Text>
				</Container>
			),
			fields: (
				<OneElementTemplate
					element={
						<div style={{ marginBottom: '2px' }}>
							<Email required />
							<Telefono />
						</div>
					}
				/>
			),
			screenIndex: index++,
			name: 'EmailTelefono',
			outputs: ['cellphone', 'email'],
			outputsText: ['cellphone', '/', 'email'],
		},
		{
			fields: <UGCConfirmacion />,
			screenIndex: index++,
			name: 'Confirmacion',
		},
	];

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
		newSteps?: Steps[];
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
			newSteps: null,
		};
	};

	useEffect(() => {
		const { firstStep, lastStep, stepsHistory, numberOfScreens, newSteps } = getFormInfo();

		const NuevoFormInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens,
			screenIndex: firstStep.screenIndex,
			formTheme: 'inverted',
			notALayer: false,
			formType: 'layer',
			step: firstStep,
			steps: newSteps ? newSteps : steps,
			stepsHistory,
			thankYouComponent: <UGCThankYou />,
		};

		initializeForm(NuevoFormInfo, initialData, true);
		trackingManager.trackFormLoad();
	}, []);

	return !steps ? null : <FormController steps={steps} screenType="layer-screen" />;
}
